import { go, tap } from 'fxjs/es';
import {
  createCanvasElement,
  makeCanvasByUrl,
  makeCanvasCutByLocation,
  makeCloneCanvas,
  makeGradientCanvas,
  rgbToHex,
} from '../../../../../../Canvas/S/util.js';
import { BpOptionKeyRingConstantS } from '../../../../../../BpOption/KeyRing/S/Constant/module/BpOptionKeyRingConstantS.js';
import { createCvImageP, makeDefaultCvImageAttrs } from '../../../../../../Maker/F/CvImage/fs.js';

import { getJustifyImageSize, resizeCanvas, trimCanvasSize } from '../../../../../../Maker/F/canvas_trim.js';
import { $qs } from 'fxdom/es';
import { makeCanvasTextured } from '../../../../../../Maker/F/cylinder.js';
import UAParser from 'ua-parser-js';
import { FileDownloader } from '../../../../../../NessLabs/F/Function/composite.utils.js';
import { UtilNumberS } from '../../../../../../Util/Number/S/Function/module/UtilNumberS.js';
import { acrylicStandEffect } from './acrylicStandEffect.js';

const parser = new UAParser();
const isIos = () => /IOS/i.test(parser.getOS()?.name);
function makeOpacity(c, opacity) {
  const canvas = createCanvasElement(c);
  const ctx = canvas.getContext('2d');
  ctx.globalAlpha = opacity;
  ctx.drawImage(c, 0, 0);
  return canvas;
}
export async function shadow(src_c, shadow_brightness = 220, ratio) {
  const step = 13 * ratio;
  const brightness = shadow_brightness;
  const hex = rgbToHex(brightness, brightness, brightness);
  const layer1 = makeCanvasTextured(src_c, hex);
  return go(layer1, async (c) => {
    if (isIos()) {
      const { glfx } = await import('./glfx.js');
      function makeCanvasBlur(canvas, blur) {
        const glfx_canvas = glfx().canvas();
        const texture = glfx_canvas.texture(canvas);
        glfx_canvas.draw(texture).triangleBlur(blur).update();
        return glfx_canvas;
      }
      const ctx = c.getContext('2d');
      ctx.globalCompositeOperation = 'destination-out';
      const x = step;
      const y = 0;
      ctx.drawImage(
        makeCanvasBlur(c, 10 * ratio * 2),
        0,
        0,
        c.width,
        c.height,
        x || 0,
        y || 0,
        c.width,
        c.height,
      );
      return c;
    } else {
      const ctx = c.getContext('2d');
      ctx.globalCompositeOperation = 'destination-out';
      const x = step;
      const y = 0;
      ctx.filter = `blur(${10 * ratio}px)`;
      ctx.drawImage(c, 0, 0, c.width, c.height, x || 0, y || 0, c.width, c.height);
      return c;
    }
  });
}
async function makeShadowAndGeometryCanvas(
  src_c,
  {
    right_solid_opacity,
    right_solid_top_hex,
    right_solid_bottom_hex,
    right_solid_center_hex,
    right_solid_x,
    right_solid_y,
  },
  shadow_brightness,
  ratio,
) {
  const c = createCanvasElement(src_c);
  const ctx = c.getContext('2d');
  go(
    makeGradientCanvas(
      c,
      [
        {
          offset: 0,
          hex: right_solid_top_hex,
        },
        {
          offset: 0.4,
          hex: right_solid_top_hex,
        },
        {
          offset: 0.5,
          hex: right_solid_center_hex,
        },
        {
          offset: 0.75,
          hex: right_solid_bottom_hex,
        },
        {
          offset: 1,
          hex: right_solid_bottom_hex,
        },
      ],
      { x1: c.width / 2, y1: 0, x2: c.width / 2, y2: c.height },
    ),
    (c) => masking({ target: c, mask: src_c }),
    (gradient_layer) => {
      const c = createCanvasElement(gradient_layer);
      const ctx = c.getContext('2d');
      ctx.drawImage(
        gradient_layer,
        0,
        0,
        gradient_layer.width,
        gradient_layer.height,
        right_solid_x * ratio,
        right_solid_y * ratio,
        gradient_layer.width,
        gradient_layer.height,
      );
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(
        gradient_layer,
        0,
        0,
        gradient_layer.width,
        gradient_layer.height,
        0,
        0,
        gradient_layer.width,
        gradient_layer.height,
      );
      return c;
    },
    (c) => makeOpacity(c, right_solid_opacity),
    (c) => ctx.drawImage(c, 0, 0),
  );

  const shadow_c = await shadow(src_c, shadow_brightness, ratio);
  ctx.drawImage(shadow_c, 0, 0);
  return c;
}

function masking({ mask, target }) {
  const canvas = makeCloneCanvas(target);
  const ctx = canvas.getContext('2d');
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(mask, 0, 0);
  return canvas;
}

function auroraAcril(src_c, { shine_opacity, shine_top_hex, shine_bottom_hex, shine_center_hex }) {
  return go(
    makeGradientCanvas(
      src_c,
      [
        {
          offset: 0,
          hex: shine_top_hex,
        },
        {
          offset: 0.3,
          hex: shine_top_hex,
        },
        {
          offset: 0.7,
          hex: shine_bottom_hex,
        },
        {
          offset: 1,
          hex: shine_bottom_hex,
        },
      ],
      { x1: 0, y1: src_c.height / 2, x2: src_c.width, y2: src_c.height / 2 },
    ),
    (c) => masking({ target: c, mask: src_c }),
    (c) => makeOpacity(c, shine_opacity),
  );
}

export async function svgDataUrlToWhiteCanvasUrl({ svg_el, width, height }) {
  svg_el.setAttributeNS(null, 'width', width);
  svg_el.setAttributeNS(null, 'height', height);
  go($qs('path', svg_el), (path_el) => {
    path_el.setAttributeNS(null, 'stroke', `#ffffff`);
    path_el.setAttributeNS(null, 'stroke-width', 1);
  });
  const svg_str2 = new XMLSerializer().serializeToString(svg_el);

  const data_url = /** @type {string} */ await new Promise((resolve, reject) => {
    const blob = new Blob([svg_str2], { type: 'image/svg+xml' });
    const file_reader = new FileReader();
    file_reader.addEventListener('error', reject);
    file_reader.addEventListener('load', () => resolve(file_reader.result));
    file_reader.readAsDataURL(blob);
  });
  const img = new Image();
  img.src = data_url;

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const canvas = createCanvasElement(img);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL());
    };
    img.onerror = reject;
  });
}

export function white(src_c, ratio) {
  const step = 2 * ratio;
  const brightness = 255;
  const hex = rgbToHex(brightness, brightness, brightness);
  const layer1 = makeCanvasTextured(src_c, hex);
  return go(layer1, (c) => {
    const ctx = c.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    const x = step;
    const y = step;
    ctx.drawImage(c, 0, 0, c.width, c.height, x || 0, y || 0, c.width, c.height);
    return c;
  });
}

async function exportResultCanvas({ c, acril_coord }) {
  const { y: top, x: left, w: width, h: height } = getJustifyImageSize({ canvas: c });
  const coord = {
    x: (acril_coord.x * c.width - left) / width,
    y: (acril_coord.y * c.height - top) / height,
  };
  const result_canvas = document.createElement('canvas');
  result_canvas.width = width;
  result_canvas.height = height;
  result_canvas.getContext('2d').drawImage(c, left, top, width, height, 0, 0, width, height);
  if (is_test) {
    console.log('트림된 결과');
    console.log(result_canvas.toDataURL());
    console.log('구멍 좌표', coord);
    console.log(drawRedCircle({ canvas: result_canvas, x: coord.x, y: coord.y, size: 3 }).toDataURL());
  }
  return { canvas: result_canvas, coord };
}
async function auroraShadowPart({ acril_canvas }, ratio) {
  return makeShadowAndGeometryCanvas(
    acril_canvas,
    {
      right_solid_opacity: 0.5,
      right_solid_top_hex: '#90bddc',
      right_solid_center_hex: '#4e4568',
      right_solid_bottom_hex: '#90bddc',
      right_solid_x: 9,
      right_solid_y: 3,
    },
    150,
    ratio,
  );
}

export async function keyringEffect({
  acril_canvas,
  design_canvas,
  material_canvas,
  material,
  acril_coord,
  ratio,
}) {
  const c = createCanvasElement(acril_canvas);
  /* 아크릴 캔버스와 동일한 크기의 캔버스 생성 */
  const ctx = c.getContext('2d');

  if (material === 'gl') {
    ctx.drawImage(
      await makeShadowAndGeometryCanvas(
        acril_canvas,
        {
          right_solid_opacity: 0.5,
          right_solid_top_hex: '#c1c1c1',
          right_solid_center_hex: '#696969',
          right_solid_bottom_hex: '#c1c1c1',
          right_solid_x: 9,
          right_solid_y: 3,
        },
        200,
        ratio,
      ),
      0,
      0,
    );
    ctx.drawImage(material_canvas, 0, 0);
    ctx.drawImage(
      await go(
        makeGradientCanvas(
          c,
          [
            {
              offset: 0,
              hex: 'rgba(200, 200, 200, 0)',
            },
            {
              offset: 0.4,
              hex: 'rgba(200, 200, 200, 0.2)',
            },
            {
              offset: 0.5,
              hex: 'rgba(255, 255, 255, 0.2)',
            },
            {
              offset: 0.6,
              hex: 'rgba(200, 200, 200, 0.2)',
            },
            {
              offset: 1,
              hex: 'rgba(200, 200, 200, 0)',
            },
          ],
          { x1: 0, y1: 0, x2: c.width, y2: c.height },
        ),
        async (c) => masking({ mask: acril_canvas, target: c }),
      ),
      0,
      0,
    );
    ctx.drawImage(design_canvas, 0, 0);
    ctx.drawImage(white(acril_canvas, ratio), 0, 0);
    return exportResultCanvas({ ctx, acril_canvas, c, ratio, acril_coord });
  } else if (material === 'au') {
    ctx.drawImage(await auroraShadowPart({ acril_canvas }, ratio), 0, 0);
    ctx.drawImage(
      auroraAcril(acril_canvas, {
        shine_opacity: 0.5,
        shine_top_hex: '#00afff',
        shine_bottom_hex: '#8666f8',
      }),
      0,
      0,
    );
    ctx.drawImage(design_canvas, 0, 0);
    ctx.drawImage(white(acril_canvas, ratio), 0, 0);
    return go(
      exportResultCanvas({ ctx, acril_canvas, c, ratio, acril_coord }),
      tap(({ canvas: result_canvas, coord }) => {
        const rc_ctx = result_canvas.getContext('2d');
        rc_ctx.drawImage(
          makeGradientCanvas(
            result_canvas,
            [
              {
                offset: 0,
                hex: 'rgba(255, 255, 255, 0)',
              },
              {
                offset: 0.4,
                hex: 'rgba(255, 255, 255, 0.1)',
              },
              {
                offset: 0.5,
                hex: 'rgba(255, 255, 255, 0.2)',
              },
              {
                offset: 0.6,
                hex: 'rgba(255, 255, 255, 0.1)',
              },
              {
                offset: 1,
                hex: 'rgba(255, 255, 255, 0)',
              },
            ],
            { x1: 0, y1: 0, x2: result_canvas.width, y2: result_canvas.height },
          ),
          0,
          0,
        );
      }),
    );
  } else {
    /* 두께 */
    ctx.drawImage(
      await makeShadowAndGeometryCanvas(
        acril_canvas,
        {
          right_solid_opacity: 0.5,
          right_solid_top_hex: '#c1c1c1',
          right_solid_center_hex: '#696969',
          right_solid_bottom_hex: '#c1c1c1',
          right_solid_x: 9,
          right_solid_y: 3,
        },
        210,
        ratio,
      ),
      0,
      0,
    );
    if (is_test) {
      console.log('두께 표현');
      console.log(acril_canvas.toDataURL());
    }
    /* 아크릴 면 */
    ctx.drawImage(
      await go(
        makeGradientCanvas(
          c,
          [
            {
              offset: 0,
              hex: 'rgba(200, 200, 200, 0)',
            },
            {
              offset: 0.4,
              hex: 'rgba(200, 200, 200, 0.2)',
            },
            {
              offset: 0.5,
              hex: 'rgba(255, 255, 255, 0.2)',
            },
            {
              offset: 0.6,
              hex: 'rgba(200, 200, 200, 0.2)',
            },
            {
              offset: 1,
              hex: 'rgba(200, 200, 200, 0)',
            },
            // {
            //   offset: 1,
            //   hex: '#ff0000',
            // },
          ],
          { x1: 0, y1: 0, x2: c.width, y2: c.height },
        ),
        async (c) => masking({ mask: acril_canvas, target: c }),
      ),
      0,
      0,
    );
    if (is_test) {
      console.log('아크릴 면 표현');
      console.log(c.toDataURL());
    }
    ctx.drawImage(design_canvas, 0, 0);
    if (is_test) {
      console.log('디자인 올리기');
      console.log(c.toDataURL());
    }
    ctx.drawImage(white(acril_canvas, ratio), 0, 0);
    if (is_test) {
      console.log('하이라이트 표현');
      console.log(c.toDataURL());
    }
    return exportResultCanvas({ ctx, acril_canvas, c, ratio, acril_coord });
  }
}

function drawRedCircle({ canvas, x, y, size }) {
  // Create a new canvas to hold the modified content
  const updatedCanvas = document.createElement('canvas');
  updatedCanvas.width = canvas.width;
  updatedCanvas.height = canvas.height;

  // Get the context of the new canvas
  const context = updatedCanvas.getContext('2d');

  // Draw the original canvas content onto the new canvas
  context.drawImage(canvas, 0, 0);

  // Convert relative coordinates to absolute coordinates
  const absoluteX = x * canvas.width;
  const absoluteY = y * canvas.height;

  // Draw a red circle at the specified coordinates
  context.beginPath();
  context.arc(absoluteX, absoluteY, size, 0, Math.PI * 2);
  context.fillStyle = 'red';
  context.fill();

  // Return the updated canvas
  return updatedCanvas;
}

function getTrimmedCanvas({ canvas, top, left, width, height }) {
  const image_data = canvas.getContext('2d').getImageData(left, top, width, height);
  const result_canvas = document.createElement('canvas');
  result_canvas.width = image_data.width;
  result_canvas.height = image_data.height;
  result_canvas.getContext('2d').putImageData(image_data, 0, 0);
  return result_canvas;
}

function getAngle({ x1, y1 }, { x2, y2 }) {
  const x = x2 - x1;
  const y = y2 - y1;
  const r = Math.atan2(y, x);
  let degree = r * (180 / Math.PI);
  if (degree < 0) {
    degree = 180 + 180 + degree;
  }
  const theta_thres = 3;
  return Math.abs(degree - 270) < theta_thres ? 270 : degree;
}
export function getR({ x1, y1 }, { x2, y2 }) {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

async function addFImage(url, option) {
  return new Promise((resolve) => {
    fabric.Image.fromURL(url, (cv_img) => {
      cv_img.set(option);
      resolve(cv_img);
    });
  });
}
async function func2(c, { x, y }, ratio) {
  const fcanvas = new fabric.StaticCanvas(document.createElement('canvas'), {
    width: c.width,
    height: c.height,
    enableRetinaScaling: false,
  });
  const f_image = await addFImage(c.toDataURL(), { top: 0, left: 0, width: c.width, height: c.height });
  fcanvas.add(f_image);
  const r = 1.5 * (300 / 25.4) * ratio;
  const top = c.height * y;
  const left = c.width * x;
  const circle = new fabric.Circle({
    top: top - r,
    left: left - r,
    radius: r,
    fill: 'green',
    globalCompositeOperation: 'destination-out',
  });
  fcanvas.add(circle);
  return fcanvas.lowerCanvasEl;
}

export async function func4(c, { x, y }) {
  const max_width = Math.sqrt(Math.pow(c.width, 2) + Math.pow(c.height, 2));
  const fcanvas = new fabric.StaticCanvas(document.createElement('canvas'), {
    width: max_width,
    height: max_width,
    enableRetinaScaling: false,
  });
  const fimg = await addFImage(c.toDataURL(), {
    width: c.width,
    height: c.height,
    top: (max_width - c.height) / 2,
    left: (max_width - c.width) / 2,
  });
  fcanvas.add(fimg);

  const point = fimg.getCenterPoint();
  const angle = getAngle({ x1: c.width / 2, y1: c.height / 2 }, { x2: x * c.width, y2: y * c.height });
  const r = getR({ x1: c.width / 2, y1: c.height / 2 }, { x2: x * c.width, y2: y * c.height });
  fimg.set('angle', 270 - angle).setCoords();
  fimg.setPositionByOrigin(point, 'center', 'center');
  return { canvas: fcanvas.lowerCanvasEl, coord: { x: 0.5, y: (fcanvas.height / 2 - r) / fcanvas.height } };
}

async function svgElToCanvas({ svg_el, width, height }) {
  svg_el.setAttributeNS(null, 'width', width);
  svg_el.setAttributeNS(null, 'height', height);
  go($qs('path', svg_el), (path_el) => {
    path_el.setAttribute('style', '');
    path_el.setAttributeNS(null, 'fill', `#ffffff`);
    path_el.setAttributeNS(null, 'stroke-width', 0);
  });
  const svg_str2 = new XMLSerializer().serializeToString(svg_el);

  const data_url = /** @type {string} */ await new Promise((resolve, reject) => {
    const blob = new Blob([svg_str2], { type: 'image/svg+xml' });
    const file_reader = new FileReader();
    file_reader.addEventListener('error', reject);
    file_reader.addEventListener('load', () => resolve(file_reader.result));
    file_reader.readAsDataURL(blob);
  });
  const img = new Image();
  img.src = data_url;

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const canvas = createCanvasElement(img);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    };
    img.onerror = reject;
  });
}

const is_test = false;
function rotateCanvas({ canvas, x, y, angle }) {
  // Create a new canvas to hold the rotated content
  const rotatedCanvas = document.createElement('canvas');
  const context = rotatedCanvas.getContext('2d');

  // Calculate the diagonal length of the original canvas to ensure nothing gets clipped
  const diagonal = Math.sqrt(canvas.width ** 2 + canvas.height ** 2);

  // Set the new canvas dimensions to fit the entire rotated content
  rotatedCanvas.width = diagonal;
  rotatedCanvas.height = diagonal;

  // Calculate the offset to center the original canvas in the new canvas
  const offsetX = (diagonal - canvas.width) / 2;
  const offsetY = (diagonal - canvas.height) / 2;

  // Translate to the center of the new canvas, apply rotation, and draw the original canvas
  context.save();
  context.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
  context.rotate((angle * Math.PI) / 180);
  context.translate(-canvas.width / 2, -canvas.height / 2);
  context.drawImage(canvas, 0, 0);

  // Transform the original (x, y) relative coordinates to the new canvas
  const absoluteX = x * canvas.width;
  const absoluteY = y * canvas.height;

  const cosAngle = Math.cos((angle * Math.PI) / 180);
  const sinAngle = Math.sin((angle * Math.PI) / 180);
  const relativeX = absoluteX - canvas.width / 2;
  const relativeY = absoluteY - canvas.height / 2;
  const rotatedX = cosAngle * relativeX - sinAngle * relativeY;
  const rotatedY = sinAngle * relativeX + cosAngle * relativeY;

  const newX = (rotatedCanvas.width / 2 + rotatedX) / rotatedCanvas.width;
  const newY = (rotatedCanvas.height / 2 + rotatedY) / rotatedCanvas.height;
  context.restore();
  // Return the new canvas and updated relative coordinates
  return {
    canvas: rotatedCanvas,
    x: newX,
    y: newY,
  };
}
export async function svgAndPngToCvImages({
  svg_el,
  design_canvas,
  hook_position_ratio,
  material,
  ratio,
  angle = 0,
}) {
  const { width, height } = design_canvas;
  const { canvas: acril_canvas, coord: acril_coord } = await go(
    svgElToCanvas({ svg_el, width, height }),
    (canvas) => func2(canvas, hook_position_ratio, ratio),
    /* 구멍 뚫기 */
    async (canvas) => {
      const {
        canvas: _canvas,
        x,
        y,
      } = rotateCanvas({
        canvas,
        x: hook_position_ratio.x,
        y: hook_position_ratio.y,
        angle,
      });
      return { canvas: _canvas, coord: { x, y } };
    },
  );
  if (is_test) {
    console.log('마진 추가하고, 각도 조절한 아크릴 캔버스');
    console.log(acril_canvas.toDataURL());
  }
  const design_result_canvas = await go(
    rotateCanvas({
      canvas: design_canvas,
      x: hook_position_ratio.x,
      y: hook_position_ratio.y,
      angle,
    }),
    async ({ canvas, x, y }) => {
      const c = makeCloneCanvas(canvas);
      const ctx = c.getContext('2d');
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(acril_canvas, 0, 0);
      return c;
    },
  );
  if (is_test) {
    console.log('디자인 캔버스에 마진 추가, 각도 조절 후 아크릴 캔버스로 마스크하기');
    console.log(design_result_canvas.toDataURL());
  }
  const material_canvas =
    material === 'gl' &&
    (await go(
      makeCanvasByUrl(BpOptionKeyRingConstantS.KEYRING_MATERIAL_GL_URL),
      (c) => {
        const cc = createCanvasElement({
          width: (11 / 2.54) * 300 * ratio,
          height: (11 / 2.54) * 300 * ratio,
        });
        const ctx = cc.getContext('2d');
        ctx.drawImage(c, 0, 0, c.width, c.height, 0, 0, cc.width, cc.height);
        return cc;
      },
      (c) => {
        return makeCanvasCutByLocation(c, {
          width,
          height,
          left: 0,
          top: 0,
        });
      },
      async (c) => func4(c, hook_position_ratio),
      async ({ canvas }) => {
        const c = canvas;
        const ctx = c.getContext('2d');
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(acril_canvas, 0, 0);
        return c;
      },
    ));
  const result = await keyringEffect({
    acril_canvas,
    design_canvas: design_result_canvas,
    material_canvas,
    material,
    acril_coord,
    ratio,
  });
  return {
    ...result,
  };
}

export async function makeBiggerHoleFiles(keyring_design_canvas, hook_position_ratio, hole_radius_cm, ratio) {
  hole_radius_cm = ratio ? hole_radius_cm * ratio : hole_radius_cm;
  const hole_margin = 0.5;
  const ctx = keyring_design_canvas.getContext('2d');
  const x = hook_position_ratio.x * keyring_design_canvas.width;
  const y = hook_position_ratio.y * keyring_design_canvas.height;
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(x, y, (UtilNumberS.mulDecimal(hole_radius_cm, hole_margin) / 2.54) * 300, 0, 2 * Math.PI);
  ctx.fill();
  return new Promise((res) => {
    keyring_design_canvas.toBlob(function (blob) {
      // Create a file object
      res({
        file: new File([blob], keyring_design_canvas.name, { type: 'image/png' }),
        width: keyring_design_canvas.width,
        canvas: keyring_design_canvas,
      });
    });
  });
}

export function svgToFile(svgElement, fileName = 'svg-file.svg', debug = false) {
  return new Promise((resolve, reject) => {
    try {
      if (debug) {
        FileDownloader.downloadSvg(svgElement, fileName);
      }

      // Serialize the SVG element to a string
      const svgString = new XMLSerializer().serializeToString(svgElement);

      // Create a Blob from the SVG string
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });

      // Resolve the promise with the Blob
      resolve(blob);
    } catch (error) {
      reject(new Error('Failed to convert SVG to file: ' + error.message));
    }
  });
}

export async function makeCvObj({ main_cv_obj }) {
  // const fitted_size = {
  //   width: Math.min(real_size.width, 100),
  //   height: Math.min(real_size.height, 100),
  // };

  // const price = BpOptionKeyRingS.makePrice(fitted_size);
  const cv_obj = await createCvImageP(main_cv_obj);
  cv_obj._data.cv_type = 'cv_image';
  // cv_obj._data.price = price.price;
  // cv_obj._data.price_en = price.price_en;
  // cv_obj._data.price_jp = price.price_jp;
  cv_obj._data.nscreened = false;
  cv_obj._data.real_width_cm = cv_obj._data.image_width_cm;
  cv_obj._data.real_height_cm = cv_obj._data.image_height_cm;
  // cv_obj.cid = 'c2323';
  return cv_obj;
}

function compositeAcrylicStandAndBottom(
  acrylic_stand_canvas,
  hook_position_ratio,
  bottom_canvas,
  bottom_center,
) {
  return go(document.createElement('canvas'), (c) => {
    const margin = Math.ceil(bottom_canvas.width / 2 + 10);
    const x_location = margin + acrylic_stand_canvas.width * hook_position_ratio.x;
    c.width = margin * 2 + acrylic_stand_canvas.width;
    c.height = acrylic_stand_canvas.height + bottom_canvas.height;
    const ctx = c.getContext('2d');
    ctx.drawImage(
      bottom_canvas,
      0,
      0,
      bottom_canvas.width,
      bottom_canvas.height,
      x_location - bottom_canvas.width * bottom_center.x,
      acrylic_stand_canvas.height * hook_position_ratio.y - bottom_canvas.height * bottom_center.y,
      bottom_canvas.width,
      bottom_canvas.height,
    );
    ctx.drawImage(
      acrylic_stand_canvas,
      0,
      0,
      acrylic_stand_canvas.width,
      // acrylic_stand_canvas.height * stand_leg_position_ratio.y + (0.3 / 2.54) * 300,
      acrylic_stand_canvas.height * hook_position_ratio.y,
      margin,
      0,
      acrylic_stand_canvas.width,
      // acrylic_stand_canvas.height * stand_leg_position_ratio.y + (0.3 / 2.54) * 300,
      acrylic_stand_canvas.height * hook_position_ratio.y,
    );
    const { top, left, width, height } = trimCanvasSize(c, 0);
    const image_data = ctx.getImageData(left, top, width, height);
    const left_of_center = x_location - left;
    const right_of_center = width - left_of_center;
    const result_canvas = document.createElement('canvas');
    const result_ctx = result_canvas.getContext('2d');
    const plus_margin = Math.abs(right_of_center - left_of_center);
    result_canvas.width = plus_margin + width;
    result_canvas.height = height;
    if (right_of_center > left_of_center) {
      result_ctx.putImageData(image_data, plus_margin, 0);
    } else {
      result_ctx.putImageData(image_data, 0, 0);
    }
    return result_canvas;
  });
}

export async function makeAcrylicFigureCvObj({
  svg_el,
  printable_canvas,
  hook_position_ratio,
  stand_bottom,
}) {
  const outline_canvas = await svgElToCanvas({
    svg_el,
    width: printable_canvas.width,
    height: printable_canvas.height,
  });
  // console.log(outline_canvas.toDataURL());
  const { width: trim_width, height: trim_height } = trimCanvasSize(outline_canvas);
  const real_size = {
    width: Math.floor((trim_width / 300) * 2.54 * 10),
    height: Math.floor((trim_height / 300) * 2.54 * 10 - 3),
  };
  const acrylic_stand_canvas = await acrylicStandEffect({
    outline_canvas,
    design_canvas: printable_canvas,
  });
  // console.log(acrylic_stand_canvas.toDataURL());
  // const stand_bottom = go(
  //   [
  //     BpOptionAcrylicFigureConstantS.SIZE_70_200,
  //     BpOptionAcrylicFigureConstantS.SIZE_140_200,
  //     BpOptionAcrylicFigureConstantS.SIZE_200_200,
  //   ],
  //   find((bottom_option) => selected_option_group.bp_option_ids.includes(bottom_option.bp_option_id)),
  //   sel('stand_bottom'),
  // );
  const original_bottom_canvas = await makeCanvasByUrl(stand_bottom.url);
  const bottom_canvas = await resizeCanvas(
    await original_bottom_canvas,
    (stand_bottom.cm_width / 10 / 2.54) * 300 * stand_bottom.ratio_for_thumbnail,
  );
  const bottom_ratio = bottom_canvas.width / original_bottom_canvas.width;

  const bottom_center = {
    x: (stand_bottom.x * bottom_ratio) / bottom_canvas.width,
    y: (stand_bottom.y * bottom_ratio) / bottom_canvas.height,
  };
  const result_canvas = compositeAcrylicStandAndBottom(
    acrylic_stand_canvas,
    hook_position_ratio,
    bottom_canvas,
    bottom_center,
  );

  const main_cv_obj = await go(
    result_canvas,
    (canvas) => {
      return {
        url: canvas.toDataURL(),
        width: canvas.width,
        height: canvas.height,
      };
    },
    (obj) => {
      obj.height_cm = Number(((obj.height / 300) * 2.54).toFixed(2));
      obj.width_cm = Number(((obj.width / 300) * 2.54).toFixed(2));
      return obj;
    },
    makeDefaultCvImageAttrs,
  );

  const cv_obj = await makeCvObj({
    main_cv_obj,
    real_size,
  });

  cv_obj._data.stand_leg_position_ratio = hook_position_ratio;
  cv_obj._data.fitted_size = real_size;
  cv_obj._data.stand_bottom_size = {
    width: stand_bottom.cm_width,
    height: stand_bottom.cm_height,
  };
  return cv_obj;
}
